import { defaultAgreement, type V2Agreement } from '../domain/agreement';
import { isHeatingFuel, isUtilitySource, type HeatingFuel, type UtilitySource } from '../domain/descriptors';
import { emptyHousePlan, planIsEmpty, type HousePlan } from '../domain/housePlan';
import { isStartMode, PROPERTY_TYPE_ORDER, type PropertyType, type StartMode } from '../domain/inspection';
import { isJurisdictionCode, type JurisdictionCode } from '../domain/jurisdiction';
import type { AlbumSaveResult, CapturedFile } from '../native/mediaContract';
import { scopedStorageKey } from '../persistence/accountScope';
import { validateAgreement } from '../persistence/schema';
import { V2_NEW_INSPECTION_DRAFT_KEY } from '../persistence/persistenceContract';
import { deleteMediaWithRecords, getMedia, putMediaWithRecords } from '../persistence/v2Database';
import { previewUrlFor, type PendingCover } from './coverPhoto';

// THE NEW INSPECTION DRAFT (Owner device test 2026-09-22 of build 2.0.0 (93): 「新建验屋没完成时退出，进去所有
// 资料都没有了，要重新填写，包括封面照片也没有了」).
//
// Until build 93 the New Inspection form lived only in React state: leaving it — a tab, Cancel, the app
// being put away and reloaded — threw away everything typed and the cover photo picked. From here on the
// form writes a draft of itself as the inspector works and reads it back when it opens again:
//   - the fields and the step go to localStorage under the ACCOUNT's key (scopedStorageKey — another
//     inspector signing in on this phone never sees them);
//   - the cover photo's bytes go to the account's media store under ONE fixed key, its description
//     (type, name, source, album outcome) beside the fields;
//   - the draft is convenience, never authority: Create clears it after the record and its cover are
//     stored, and 清空重填 clears it on the inspector's say-so. An untouched form leaves nothing behind.
export const NEW_INSPECTION_DRAFT_COVER_KEY = 'new-inspection-draft-cover';

export type DraftStep = 1 | 2 | 3 | 4;

export interface NewInspectionDraftCover {
  mimeType: string;
  fileName: string;
  source: CapturedFile['source'];
  album: AlbumSaveResult | null;
}

export interface NewInspectionDraft {
  version: 1;
  savedAt: number;
  step: DraftStep;
  address: string;
  city: string;
  state: JurisdictionCode | null;
  zip: string;
  propertyType: PropertyType | null;
  yearBuilt: string;
  previouslyOccupied: boolean | null;
  unitCount: number | null;
  heatingFuel: HeatingFuel | null;
  waterSupplySource: UtilitySource | null;
  wasteDisposalSource: UtilitySource | null;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  inspectionAt: string;
  inspectorName: string;
  weather: string;
  agreement: V2Agreement;
  plan: HousePlan;
  startMode: StartMode | null;
  cover: NewInspectionDraftCover | null;
}

const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const boolOrNull = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

// The plan and the agreement are the form's own objects, JSON round-tripped; a shape that is not theirs
// (an older draft, a hand-edited store) falls back to the empty value the form starts with.
function planFrom(value: unknown): HousePlan {
  if (!isRecord(value) || !Array.isArray(value.floors) || !isRecord(value.spaces) || !isRecord(value.systems) || !isRecord(value.basement) || !Array.isArray(value.others)) return emptyHousePlan();
  return value as unknown as HousePlan;
}

// The agreement is read back field by field, exactly like the one stored in an inspection record
// (validateAgreement). TestFlight 211, 2026-10-28: a draft written before the agreement copy of 2026-10-19
// had no additionalServices, and the cast it used to get here blanked the whole app at step 4.
function agreementFrom(value: unknown): V2Agreement {
  if (!isRecord(value) || typeof value.documentVersion !== 'string' || typeof value.demo !== 'boolean' || !isRecord(value.client) || !isRecord(value.inspector) || !isRecord(value.fee)) return defaultAgreement();
  return validateAgreement(value);
}

// A cover on this form comes from the camera or the library and from nowhere else.
function coverFrom(value: unknown): NewInspectionDraftCover | null {
  if (!isRecord(value) || typeof value.mimeType !== 'string' || typeof value.fileName !== 'string') return null;
  if (value.source !== 'camera' && value.source !== 'photoLibrary') return null;
  const album = isRecord(value.album) && typeof value.album.state === 'string' && typeof value.album.detail === 'string' ? (value.album as unknown as AlbumSaveResult) : null;
  return { mimeType: value.mimeType, fileName: value.fileName, source: value.source, album };
}

export function readNewInspectionDraft(): NewInspectionDraft | null {
  let raw: string | null = null;
  try {
    const key = scopedStorageKey(V2_NEW_INSPECTION_DRAFT_KEY);
    raw = key === null ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== 1) return null;
  const unitCount = typeof parsed.unitCount === 'number' && Number.isInteger(parsed.unitCount) && parsed.unitCount > 0 ? parsed.unitCount : null;
  return {
    version: 1,
    savedAt: typeof parsed.savedAt === 'number' && Number.isFinite(parsed.savedAt) ? parsed.savedAt : 0,
    step: parsed.step === 2 || parsed.step === 3 || parsed.step === 4 ? parsed.step : 1,
    address: str(parsed.address),
    city: str(parsed.city),
    state: isJurisdictionCode(parsed.state) ? parsed.state : null,
    zip: str(parsed.zip),
    propertyType: typeof parsed.propertyType === 'string' && (PROPERTY_TYPE_ORDER as readonly string[]).includes(parsed.propertyType) ? (parsed.propertyType as PropertyType) : null,
    yearBuilt: str(parsed.yearBuilt),
    previouslyOccupied: boolOrNull(parsed.previouslyOccupied),
    unitCount,
    heatingFuel: isHeatingFuel(parsed.heatingFuel) ? parsed.heatingFuel : null,
    waterSupplySource: isUtilitySource(parsed.waterSupplySource) ? parsed.waterSupplySource : null,
    wasteDisposalSource: isUtilitySource(parsed.wasteDisposalSource) ? parsed.wasteDisposalSource : null,
    clientName: str(parsed.clientName),
    clientPhone: str(parsed.clientPhone),
    clientEmail: str(parsed.clientEmail),
    inspectionAt: str(parsed.inspectionAt),
    inspectorName: str(parsed.inspectorName),
    weather: str(parsed.weather),
    agreement: agreementFrom(parsed.agreement),
    plan: planFrom(parsed.plan),
    startMode: isStartMode(parsed.startMode) ? parsed.startMode : null,
    cover: coverFrom(parsed.cover)
  };
}

function agreementTouched(agreement: V2Agreement): boolean {
  return (
    agreement.demo ||
    agreement.fee.amount.trim().length > 0 ||
    agreement.clientAiConsent ||
    agreement.client.name.trim().length > 0 ||
    agreement.inspector.name.trim().length > 0 ||
    agreement.client.signature !== null ||
    agreement.inspector.signature !== null ||
    agreement.signedAt !== null
  );
}

// A form nobody has touched leaves nothing behind. The values the form fills in by itself — the date, the
// inspector's own name, the step — are not content.
export function draftHasContent(draft: NewInspectionDraft): boolean {
  return (
    [draft.address, draft.city, draft.zip, draft.yearBuilt, draft.clientName, draft.clientPhone, draft.clientEmail, draft.weather].some((s) => s.trim().length > 0) ||
    draft.state !== null ||
    draft.propertyType !== null ||
    draft.previouslyOccupied !== null ||
    draft.unitCount !== null ||
    draft.heatingFuel !== null ||
    draft.waterSupplySource !== null ||
    draft.wasteDisposalSource !== null ||
    draft.startMode !== null ||
    draft.cover !== null ||
    !planIsEmpty(draft.plan) ||
    agreementTouched(draft.agreement)
  );
}

export function writeNewInspectionDraft(draft: NewInspectionDraft): void {
  try {
    const key = scopedStorageKey(V2_NEW_INSPECTION_DRAFT_KEY);
    if (key === null) return;
    if (!draftHasContent(draft)) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // storage refusal must never block field work; the form keeps working from memory
  }
}

function clearDraftFields(): void {
  try {
    const key = scopedStorageKey(V2_NEW_INSPECTION_DRAFT_KEY);
    if (key !== null) window.localStorage.removeItem(key);
  } catch {
    // nothing stored, nothing to clear
  }
}

// Fields first (synchronous, so a form that opens right after this reads nothing), then the cover bytes.
export async function clearNewInspectionDraft(): Promise<void> {
  clearDraftFields();
  try {
    await deleteMediaWithRecords(NEW_INSPECTION_DRAFT_COVER_KEY);
  } catch {
    // no bytes to drop, or no account open: nothing is kept either way
  }
}

export function describeDraftCover(pending: PendingCover): NewInspectionDraftCover {
  return { mimeType: pending.file.mimeType, fileName: pending.file.fileName, source: pending.file.source, album: pending.album };
}

// The picked cover's bytes, as picked (the print-size resize happens when the inspection exists).
export async function storeDraftCover(pending: PendingCover): Promise<void> {
  await putMediaWithRecords(NEW_INSPECTION_DRAFT_COVER_KEY, { type: pending.file.mimeType || pending.file.blob.type, bytes: await pending.file.blob.arrayBuffer() }, null);
}

export async function removeDraftCover(): Promise<void> {
  await deleteMediaWithRecords(NEW_INSPECTION_DRAFT_COVER_KEY);
}

export async function loadDraftCover(cover: NewInspectionDraftCover): Promise<PendingCover | null> {
  const blob = await getMedia(NEW_INSPECTION_DRAFT_COVER_KEY, 'full');
  if (!blob) return null;
  const file: CapturedFile = { blob, mimeType: cover.mimeType || blob.type, fileName: cover.fileName, source: cover.source };
  return { file, album: cover.album, previewUrl: previewUrlFor(file) };
}
